import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Loader2, ReceiptText, Calendar, Search,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useExpenses } from '@/hooks/useExpenses';
import { ExpenseForm } from '@/components/expenses/ExpenseForm';
import { ExpenseDetailSheet } from '@/components/expenses/ExpenseDetailSheet';
import { ExpenseStatusBadge, SyncStatusBadge } from '@/components/expenses/ExpenseStatusBadge';

type DateFilter = 'today' | 'week' | 'month' | 'all';

function dateRangeFor(f: DateFilter): { dateFrom?: string; dateTo?: string } {
  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  if (f === 'all') return {};
  if (f === 'today') return { dateFrom: iso(today), dateTo: iso(today) };
  if (f === 'week') {
    const start = new Date(today); start.setDate(today.getDate() - 6);
    return { dateFrom: iso(start), dateTo: iso(today) };
  }
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return { dateFrom: iso(start), dateTo: iso(today) };
}

export default function ExpensesPage() {
  const { t } = useTranslation();
  const [dateFilter, setDateFilter] = useState<DateFilter>('month');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [openExpenseId, setOpenExpenseId] = useState<string | null>(null);

  const range = dateRangeFor(dateFilter);
  const { data, isLoading } = useExpenses({
    ...range,
    search: search || undefined,
    page,
    pageSize: 25,
  });

  const totalAmount = (data?.items ?? []).reduce(
    (sum, e) => sum + Number(e.amount_sar), 0
  );

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-6">
      <div>
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
          <ArrowLeft className="h-4 w-4" />
          {t('back', 'Back')}
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <ReceiptText className="h-6 w-6" />
              {t('expense.title_plural', 'Expenses')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('expense.subtitle', 'Track operational expenses with full audit trail and Zoho sync.')}
            </p>
          </div>
          <Button onClick={() => setShowForm(true)} variant="gold">
            <Plus className="h-4 w-4 mr-2" />
            {t('expense.new', 'New expense')}
          </Button>
        </div>
      </div>

      {/* Date range filter buttons */}
      <div className="flex gap-2 flex-wrap items-center">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        {(['today', 'week', 'month', 'all'] as DateFilter[]).map(f => (
          <Button
            key={f}
            size="sm"
            variant={dateFilter === f ? 'default' : 'outline'}
            onClick={() => { setDateFilter(f); setPage(1); }}
          >
            {t(`date.${f}`, f === 'today' ? 'Today' : f === 'week' ? '7 days' : f === 'month' ? 'This month' : 'All')}
          </Button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={t('expense.search_placeholder', 'Search code, vendor, invoice...')}
            className="w-64"
          />
        </div>
      </div>

      {/* Total summary */}
      {data && data.items.length > 0 && (
        <div className="rounded-md bg-muted/40 px-4 py-2 text-sm text-muted-foreground">
          {t('expense.total_summary', '{{count}} expenses · Total {{total}} SAR', {
            count: data.items.length,
            total: totalAmount.toLocaleString(),
          })}
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <ReceiptText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-medium">{t('expense.empty_title', 'No expenses found')}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t('expense.empty_desc', 'Click "New expense" to log your first one.')}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">{t('expense.col_code', 'Code')}</th>
                <th className="text-left px-3 py-2">{t('expense.col_date', 'Date')}</th>
                <th className="text-left px-3 py-2">{t('expense.col_category', 'Category')}</th>
                <th className="text-left px-3 py-2">{t('expense.col_vendor', 'Vendor')}</th>
                <th className="text-right px-3 py-2">{t('expense.col_amount', 'Amount')}</th>
                <th className="text-left px-3 py-2">{t('expense.col_status', 'Status')}</th>
                <th className="text-left px-3 py-2">{t('expense.col_sync', 'Sync')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(e => (
                <tr
                  key={e.id}
                  onClick={() => setOpenExpenseId(e.id)}
                  className="border-t border-border hover:bg-muted/40 cursor-pointer"
                >
                  <td className="px-3 py-2 font-mono text-xs">{e.code}</td>
                  <td className="px-3 py-2">{new Date(e.expense_date).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-xs">{e.category_code ?? '—'}</td>
                  <td className="px-3 py-2 text-xs truncate max-w-[140px]">{e.vendor_name_raw ?? '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{Number(e.amount_sar).toLocaleString()}</td>
                  <td className="px-3 py-2"><ExpenseStatusBadge status={e.status} /></td>
                  <td className="px-3 py-2"><SyncStatusBadge status={e.sync_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {data.total > data.pageSize && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/30 text-xs">
              <span className="text-muted-foreground">
                {t('expense.page_of', 'Page {{page}} of {{total}}', {
                  page,
                  total: Math.ceil(data.total / data.pageSize),
                })}
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  ←
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page * data.pageSize >= data.total}
                  onClick={() => setPage(page + 1)}
                >
                  →
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <ExpenseForm open={showForm} onClose={() => setShowForm(false)} />
      <ExpenseDetailSheet expenseId={openExpenseId} onClose={() => setOpenExpenseId(null)} />
    </div>
  );
}
